import { useInfiniteQuery } from "@tanstack/react-query";
import { adGroupService } from "../api/services/adGroupService";
import type {
  AdGroup,
  AdGroupFilters,
  AdGroupsResponse,
} from "../schemas/adGroupSchema";
import { useAdGroups } from "./useAdGroups";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type AdGroupsInfiniteFilters = Omit<
  AdGroupFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useAdGroupsInfinite = (filters: AdGroupsInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useAdGroups(undefined, { enabled: false });

  const query = useInfiniteQuery<AdGroupsResponse, Error>({
    queryKey: ["adGroups", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      adGroupService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const adGroups: AdGroup[] = query.data?.pages.flatMap((p) => p.adGroups) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    adGroups,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
